import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useBranches } from '../hooks/useBranches';
import { usePrintHeader } from '../hooks/usePrintHeader';
import { Icons } from '../components/nav/icons';
import { TopNav } from '../components/nav/TopNav';
import { CommandPalette } from '../components/nav/CommandPalette';
import { RateStrip } from '../components/nav/RateStrip';
import { useIdleSession } from '../hooks/useIdleSession';
import { RateMaster, useShouldPromptRates } from '../components/RateMaster';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { SECTIONS } from '../components/nav/navigation';
import { usePermission } from '../hooks/usePermission';

/**
 * The shell — a top bar, the way an Indian business site is laid out.
 *
 * Two rows on a wide screen: the dark bar carries the shop's name, the whole
 * menu, and the account; the thin light strip under it carries today's rate,
 * the branch and the date — the things a counter checks without asking. On a
 * phone the menu collapses to a drawer, because dropdowns do not work under a
 * thumb.
 *
 * The rail is gone. It listed thirty items down the side and made a jeweller
 * hunt; a bar with the everyday screens flat and the rest grouped is what the
 * shops this is for already know how to read.
 */
export function AppLayout() {
  const { session, signOut } = useAuth();
  const { branches } = useBranches();
  const { header } = usePrintHeader();
  const navigate = useNavigate();
  const location = useLocation();

  const { secondsLeft, stayIn } = useIdleSession();
  const [palette, setPalette] = useState(false);
  const [rateMaster, setRateMaster] = useState(false);
  const { can: may } = usePermission();

  // Check the morning rate board once, on load, and pop it if today's rates are
  // not set — the jeweller's first task of the day.
  const { data: board } = useQuery({
    queryKey: ['rate-board'],
    queryFn: () => api.get<{ data: Parameters<typeof useShouldPromptRates>[0] }>('/rates/board'),
    staleTime: 300_000,
  });
  const shouldPrompt = useShouldPromptRates(board?.data, may('rate.create'));
  useEffect(() => {
    if (shouldPrompt) setRateMaster(true);
  }, [shouldPrompt]);
  const [drawer, setDrawer] = useState(false);
  const [account, setAccount] = useState(false);

  const shopName = header.companyName || 'Aurum';

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    setDrawer(false);
    setAccount(false);
  }, [location.pathname]);

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const out = (): void => {
    signOut();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* The menu bar. */}
      <header className="no-print sticky top-0 z-30 border-b border-silver-100 bg-surface text-ink shadow-card">
        <div className="mx-auto flex h-14 max-w-[1500px] items-center gap-3 px-3">
          <button
            className="rounded p-1 hover:bg-silver-50 lg:hidden"
            onClick={() => setDrawer(true)}
            aria-label="Menu"
          >
            <Icons.menu />
          </button>

          {/* The shop's own name is the brand here, not the software's. */}
          <button
            className="flex shrink-0 items-center gap-2 pr-2 text-left"
            onClick={() => navigate('/')}
            title={shopName}
          >
            {header.logoDataUri ? (
              <img src={header.logoDataUri} alt="" className="h-9 w-auto max-w-[120px] object-contain" />
            ) : (
              <span className="grid h-9 w-9 place-items-center rounded-md bg-velvet text-base font-bold text-rail-active">
                {shopName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="hidden leading-tight sm:block">
              <span className="block text-base font-bold tracking-tight text-velvet">{shopName}</span>
              {header.branchName ? (
                <span className="block text-2xs uppercase tracking-wider text-ink-soft">{header.branchName}</span>
              ) : null}
            </span>
          </button>

          {/* The whole menu, on wide screens. */}
          <nav className="ml-auto hidden min-w-0 lg:block">
            <TopNav />
          </nav>

          <div className="flex items-center gap-2 pl-2">
            <button
              className="flex items-center gap-1.5 rounded border border-silver-100 px-2 py-1 text-2xs text-ink-soft hover:border-velvet-600 hover:text-velvet"
              onClick={() => setPalette(true)}
            >
              <Icons.search />
              <span className="hidden sm:inline">Search</span>
              <kbd className="hidden rounded border border-silver-100 px-1 sm:inline">⌘K</kbd>
            </button>

            <div className="relative">
              <button
                className="flex items-center gap-1.5 rounded px-2 py-1 text-sm hover:bg-silver-50"
                onClick={() => setAccount((a) => !a)}
              >
                <span className="grid h-6 w-6 place-items-center rounded-full bg-velvet text-2xs font-semibold text-rail-active">
                  {session?.user.fullName?.slice(0, 1).toUpperCase() ?? '?'}
                </span>
                <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden="true">
                  <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>

              {account ? (
                <div className="absolute right-0 top-full z-40 mt-1 w-56 overflow-hidden rounded-md border border-gold-200 bg-white shadow-pop">
                  <div className="border-b border-silver-100 px-3 py-2">
                    <p className="text-sm font-medium text-ink">{session?.user.fullName}</p>
                    <p className="text-2xs text-ink-soft">{session?.roles[0]?.replace(/_/g, ' ').toLowerCase()}</p>
                  </div>
                  <button
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-gold-50"
                    onClick={() => navigate('/account')}
                  >
                    <Icons.user /> My account
                  </button>
                  <button
                    className="w-full px-3 py-2 text-left text-sm text-ink-soft hover:bg-gold-50 hover:text-ink"
                    onClick={out}
                  >
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* The status strip: rate, branch, date. */}
        <div className="border-t border-rail-line bg-rail px-3">
          <div className="mx-auto flex h-8 max-w-[1500px] items-center gap-4 text-rail-ink">
            <RateStrip />
            <div className="ml-auto flex items-center gap-4 text-2xs text-rail-ink/75">
              {branches.length > 1 ? <span>{branches.length} branches</span> : null}
              <span className="num">{today}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer — the same grouped menu, stacked for a thumb. */}
      {drawer ? (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setDrawer(false)}>
          <div className="absolute inset-0 bg-rail/50" />
          <div
            className="absolute inset-y-0 left-0 w-64 overflow-y-auto bg-white py-2 text-ink"
            onClick={(e) => e.stopPropagation()}
          >
            <MobileMenu onGo={(to) => navigate(to)} />
          </div>
        </div>
      ) : null}

      {secondsLeft !== null ? (
        <div className="no-print flex items-center gap-3 border-b border-warn/30 bg-warn/10 px-4 py-2 text-sm">
          <span>
            Signing out in <span className="num font-semibold">{secondsLeft}</span> seconds — the till has been idle.
          </span>
          <button className="btn-ghost ml-auto py-1 text-2xs" onClick={stayIn}>
            I am still here
          </button>
        </div>
      ) : null}

      <main className="mx-auto w-full max-w-[1500px] flex-1 px-4 py-5 lg:px-6">
        {blockedBy(location.pathname, may) ? <NoAccess page={blockedBy(location.pathname, may)!} /> : <Outlet />}
      </main>

      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      <RateMaster open={rateMaster} onClose={() => setRateMaster(false)} />
    </div>
  );
}

/** The grouped menu, stacked for the mobile drawer — a thumb reads a list better than a bar. */
/**
 * The page a person may not use, named — or null when they may.
 *
 * The menu already says which permission each page needs, and hides what a
 * person cannot open. Typing the address, or following an old link, used to
 * land them on a half-built page: empty tables, a "Record expense" button that
 * would only be refused, and a run of failed requests. The same rule the menu
 * follows now decides the page.
 */
function blockedBy(pathname: string, may: (p: string) => boolean): string | null {
  // Home is where "back" goes; it guards its own panels.
  if (pathname === '/') return null;
  const items = SECTIONS.flatMap((s) => s.items).filter((i) => i.to.split('?')[0] === pathname && i.permission);
  if (items.length === 0) return null;
  return items.some((i) => may(i.permission!)) ? null : items[0]!.label;
}

function NoAccess({ page }: { page: string }): JSX.Element {
  const navigate = useNavigate();
  return (
    <div className="mx-auto mt-10 max-w-md rounded-xl border border-silver-100 bg-surface p-6 text-center shadow-card">
      <p className="text-base font-semibold">{page} is not open to you</p>
      <p className="mt-1 text-sm text-ink-soft">
        Your role does not include it. The owner or an administrator can give access from Setup → Users.
      </p>
      <button className="btn-primary mt-4" onClick={() => navigate('/')}>
        Back to home
      </button>
    </div>
  );
}

function MobileMenu({ onGo }: { onGo: (to: string) => void }): JSX.Element {
  const { can } = usePermission();

  return (
    <>
      {SECTIONS.map((section) => {
        const items = section.items.filter((i) => !i.permission || can(i.permission));
        if (items.length === 0) return null;
        return (
          <div key={section.heading} className="mb-1">
            <p className="px-3 pb-0.5 pt-2 text-2xs font-medium uppercase tracking-wider text-ink-soft">
              {section.heading}
            </p>
            {items.map((item) => {
              const Icon = Icons[item.icon];
              return (
                <button
                  key={item.to}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-gold-50"
                  onClick={() => onGo(item.to)}
                >
                  <span className="text-ink-soft">
                    <Icon />
                  </span>
                  {item.label}
                </button>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
